import type { ReactNode } from 'react';
import { useState } from 'react';

import { ScreenReaderOnly } from '@capsule/consumer-design-system';

import classNames from 'classnames';
import OutsideClickHandler from 'react-outside-click-handler';

import { TransparentButton } from 'src/app/components/Elements/Button/TransparentButton';
import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { CAPSULE_ICON_STRINGS } from 'src/consts';
import { CARD_MENU, CARD_MENU_BUTTON, CARD_MENU_POPUP_CONTAINER } from 'src/consts/testIDs';

import { FocusLockAndEscape } from './FocusLockAndEscape';
import { PopupButton, PopupButtonText, PopupContainer } from './styles';
import styles from './styles.module.scss';

const SelectPopUp = ({ items, hideSelectMenu }) => {
  const handleClick = (e, item) => {
    // We were not hiding the menu before after a user clicks a popup.
    hideSelectMenu();
    item.action();
    e.stopPropagation();
  };

  return (
    <OutsideClickHandler onOutsideClick={hideSelectMenu}>
      <PopupContainer data-testid={CARD_MENU_POPUP_CONTAINER}>
        {items.map((item, i) => {
          return (
            <PopupButton
              key={i}
              type="button"
              onClick={e => handleClick(e, item)}
              data-testid={`${CARD_MENU}_${item.label}`}
            >
              <PopupButtonText>{item.label}</PopupButtonText>
            </PopupButton>
          );
        })}
      </PopupContainer>
    </OutsideClickHandler>
  );
};

export const PaymentCardItem = ({ children, className }: { children: ReactNode; className?: string }) => {
  return <div className={classNames(styles.component, className)}>{children}</div>;
};

export type MenuActions = {
  openMenuAction?: (action?: () => void) => void;
  items: { label: string; analyticsPayload?: { patientmedication_id: number }; action: () => Promise<void> }[];
};

interface CardItemOverflowMenuProps {
  menuActions: MenuActions;
  className?: string;
}

export const CardItemOverflowMenu = ({ className, menuActions }: CardItemOverflowMenuProps) => {
  const [isMenuVisible, setMenuVisible] = useState(false);

  const showMenu: MenuActions['openMenuAction'] = action => {
    if (action) action();
    setMenuVisible(true);
  };

  const hideMenu = () => setMenuVisible(false);

  return (
    <div className={classNames(styles.modify, className)} data-cy="selectMenu">
      <TransparentButton
        role="switch"
        aria-pressed={isMenuVisible}
        onClick={e => {
          showMenu(menuActions.openMenuAction);
          e.stopPropagation();
        }}
        data-testid={CARD_MENU_BUTTON}
      >
        <ScreenReaderOnly>Open menu for this medication</ScreenReaderOnly>
        <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.system_page_1} />
      </TransparentButton>

      {isMenuVisible && (
        <FocusLockAndEscape handleEscapePressed={hideMenu}>
          <SelectPopUp hideSelectMenu={hideMenu} items={menuActions.items} />
        </FocusLockAndEscape>
      )}
    </div>
  );
};
